/**
 * PDF Generation Service
 * Generates PDF documents with FairCare Plus company template
 */

import { Document } from '@/models/document.model';
import { jsPDF } from 'jspdf';

export class PdfService {
  /**
   * Generate PDF from document with company template
   */
  static async generateDocumentPdf(document: Document): Promise<Blob> {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });

    // Get document content from metadata
    const content = (document.metadata?.fullContent as string) || document.description || '';
    const version = document.version || 1;
    const lastUpdated = document.metadata?.lastUpdated as string || 
                       document.updatedAt.toLocaleDateString();

    // Header with company branding
    pdf.setFillColor(0, 102, 204); // Blue background
    pdf.rect(0, 0, 216, 30, 'F');
    
    // Company logo area (placeholder - you can add actual logo image)
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(24);
    pdf.setFont('helvetica', 'bold');
    pdf.text('FairCare Plus', 20, 20);
    
    // Reset text color for content
    pdf.setTextColor(0, 0, 0);

    // Document title
    pdf.setFillColor(255, 255, 255);
    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    
    const titleY = 45;
    pdf.text(document.name, 20, titleY);

    // Document metadata
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    
    let metadataY = titleY + 10;
    if (document.description) {
      pdf.setFontSize(11);
      pdf.setTextColor(60, 60, 60);
      const descriptionLines = pdf.splitTextToSize(document.description, 176);
      pdf.text(descriptionLines, 20, metadataY);
      metadataY += descriptionLines.length * 6;
    }

    metadataY += 5;
    pdf.setFontSize(9);
    pdf.setTextColor(100, 100, 100);
    
    const metadata: string[] = [];
    if (document.category) {
      metadata.push(`Category: ${document.category}`);
    }
    metadata.push(`Version: ${version}`);
    metadata.push(`Last Updated: ${lastUpdated}`);
    if (document.status) {
      metadata.push(`Status: ${document.status.replace('_', ' ')}`);
    }
    
    pdf.text(metadata.join(' • '), 20, metadataY);
    metadataY += 8;

    // Divider line
    pdf.setDrawColor(200, 200, 200);
    pdf.line(20, metadataY, 196, metadataY);
    metadataY += 10;

    // Document content
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    
    // Process content - handle plain text and convert to PDF
    const contentLines = this.processContentForPdf(content);
    let currentY = metadataY;
    const pageHeight = 279; // A4 height in mm
    const margin = 20;
    const maxWidth = 176;
    const lineHeight = 6;

    for (const line of contentLines) {
      // Check if we need a new page
      if (currentY > pageHeight - 30) {
        pdf.addPage();
        currentY = margin;
      }

      // Handle different line types
      if (line.type === 'heading') {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        currentY += 5;
        const headingLines = pdf.splitTextToSize(line.text, maxWidth);
        pdf.text(headingLines, margin, currentY);
        currentY += headingLines.length * 7;
      } else if (line.type === 'subheading') {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        currentY += 4;
        const subheadingLines = pdf.splitTextToSize(line.text, maxWidth);
        pdf.text(subheadingLines, margin, currentY);
        currentY += subheadingLines.length * 6;
      } else {
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'normal');
        const textLines = pdf.splitTextToSize(line.text, maxWidth);
        pdf.text(textLines, margin, currentY);
        currentY += textLines.length * lineHeight;
      }
      
      currentY += 2; // Spacing between lines
    }

    // Footer on each page
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150, 150, 150);
      const footerText = `Page ${i} of ${pageCount} | FairCare Plus Operations Portal | Confidential`;
      const footerWidth = pdf.getTextWidth(footerText);
      pdf.text(
        footerText,
        (216 - footerWidth) / 2,
        pageHeight - 10
      );
      pdf.text(
        `Generated: ${new Date().toLocaleDateString()}`,
        196,
        pageHeight - 10,
        { align: 'right' }
      );
    }

    // Generate blob
    const blob = pdf.output('blob');
    return blob;
  }

  /**
   * Process document content for PDF formatting
   */
  private static processContentForPdf(content: string): Array<{ type: 'text' | 'heading' | 'subheading'; text: string }> {
    const lines: Array<{ type: 'text' | 'heading' | 'subheading'; text: string }> = [];
    const contentLines = content.split('\n');

    for (const line of contentLines) {
      const trimmed = line.trim();
      if (!trimmed) {
        lines.push({ type: 'text', text: '' });
        continue;
      }

      // Detect headings (lines that are all caps or start with numbers)
      if (trimmed.match(/^[A-Z\s]{10,}$/) || trimmed.match(/^\d+\.\s+[A-Z]/)) {
        lines.push({ type: 'heading', text: trimmed });
      } 
      // Detect subheadings (lines ending with colon or short lines in caps)
      else if (trimmed.endsWith(':') && trimmed.length < 50) {
        lines.push({ type: 'subheading', text: trimmed });
      } 
      // Regular text
      else {
        lines.push({ type: 'text', text: trimmed });
      }
    }

    return lines;
  }

  /**
   * Download PDF file
   */
  static downloadPdf(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Generate and download PDF for a document
   */
  static async downloadDocumentAsPdf(document: Document): Promise<void> {
    try {
      const blob = await this.generateDocumentPdf(document);
      const filename = document.fileName.replace(/\.txt$/, '') || document.name.replace(/\s+/g, '_');
      this.downloadPdf(blob, filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw new Error('Failed to generate PDF document');
    }
  }
}
